import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CommandBar } from '../components/CommandBar';
import { ContextMenu } from '../components/ContextMenu';
import { MoveScopeDialog } from '../components/MoveScopeDialog';
import { NewTaskModal } from '../components/NewTaskModal';
import { TaskDetail } from '../components/TaskDetail';
import { TaskList } from '../components/TaskList';
import { ViewHeader } from '../components/ViewHeader';
import { useAuth } from '../context/AuthContext';
import { useUndo } from '../context/UndoContext';
import { useTaskContextMenu } from '../hooks/useTaskContextMenu';
import { useTaskKeyboard } from '../hooks/useTaskKeyboard';
import { useTaskPolling } from '../hooks/useTaskPolling';
import { searchTasks } from '../utils/searchTasks';
import type { Scope } from '../scopes';
import type { Task, TaskPriority } from '../types';
import { sortTasks } from '../utils/sort';
import * as api from '../utils/apiClient';

export function PrivateView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedId = searchParams.get('t');
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [query, setQuery] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);
  const undo = useUndo();
  const ctx = useTaskContextMenu();

  const refresh = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const ts = await api.listTasks('all');
      setTasks(ts);
      setError(null);
    } catch (err) {
      if (showSpinner) setError((err as Error).message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(true);
  }, [refresh]);

  useTaskPolling(() => {
    void refresh();
  });

  const privateTasks = useMemo(
    () =>
      tasks.filter(
        (t) => t.private === true && t.createdBy === user?.userId && !t.deletedAt
      ),
    [tasks, user]
  );

  const filtered = useMemo(() => searchTasks(privateTasks, query), [privateTasks, query]);
  const sorted = useMemo(() => sortTasks(filtered), [filtered]);
  const selected = sorted.find((t) => t.taskId === selectedId) ?? null;
  const selectedIdx = selected ? sorted.findIndex((t) => t.taskId === selected.taskId) : -1;

  const select = useCallback(
    (taskId: string | null) => {
      const wasSelected = searchParams.has('t');
      const willSelect = !!taskId;
      if (wasSelected && !willSelect) {
        navigate(-1);
        return;
      }
      const next = new URLSearchParams(searchParams);
      if (taskId) next.set('t', taskId);
      else next.delete('t');
      const replace = wasSelected && willSelect;
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams, navigate]
  );

  const upsertTask = useCallback((task: Task) => {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.taskId === task.taskId);
      if (idx < 0) return [...prev, task];
      const next = [...prev];
      next[idx] = task;
      return next;
    });
  }, []);

  const removeLocalTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
  }, []);

  const onUpdate = useCallback(
    async (patch: Parameters<typeof api.updateTask>[2]) => {
      if (!selected) return;
      const updated = await api.updateTask(selected.scope, selected.taskId, patch);
      upsertTask(updated);
    },
    [selected, upsertTask]
  );

  const onMove = useCallback(
    async (toScope: Scope) => {
      if (!selected) return;
      await api.moveTask(selected.scope, selected.taskId, toScope);
      removeLocalTask(selected.taskId);
      select(null);
    },
    [selected, removeLocalTask, select]
  );

  const deleteWithUndo = useCallback(
    (task: Task) => {
      removeLocalTask(task.taskId);
      if (selected?.taskId === task.taskId) select(null);
      undo.queue({
        message: `Deleted: ${task.title}`,
        reverse: () => upsertTask(task),
        onExpire: async () => {
          try {
            await api.deleteTask(task.scope, task.taskId);
          } catch {
            /* best effort */
          }
        },
      });
    },
    [removeLocalTask, selected, select, undo, upsertTask]
  );

  const onDelete = useCallback(() => {
    if (!selected) return;
    deleteWithUndo(selected);
  }, [selected, deleteWithUndo]);

  const onAddNote = useCallback(
    async (body: string) => {
      if (!selected) return;
      const updated = await api.addNote(selected.scope, selected.taskId, body);
      upsertTask(updated);
    },
    [selected, upsertTask]
  );

  const createTask = useCallback(
    async (input: {
      scope: Scope;
      title: string;
      priority?: TaskPriority;
      dueDate?: string;
      note?: string;
    }) => {
      const { note, ...taskInput } = input;
      const created = await api.createTask({
        ...taskInput,
        assigneeId: user?.userId,
        private: true,
      });
      const final = note ? await api.addNote(created.scope, created.taskId, note) : created;
      upsertTask(final);
      select(final.taskId);
    },
    [upsertTask, select, user]
  );

  useTaskKeyboard(
    {
      next: () => {
        if (sorted.length === 0) return;
        const nextIdx = Math.min(sorted.length - 1, selectedIdx < 0 ? 0 : selectedIdx + 1);
        select(sorted[nextIdx].taskId);
      },
      prev: () => {
        if (sorted.length === 0) return;
        const prevIdx = Math.max(0, selectedIdx < 0 ? 0 : selectedIdx - 1);
        select(sorted[prevIdx].taskId);
      },
      close: () => select(null),
      complete: () => {
        if (!selected) return;
        void onUpdate({ status: 'completed' });
      },
      togglePin: () => void onUpdate({ pinned: !selected?.pinned }),
      snooze: () => {
        if (!selected) return;
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        void onUpdate({ dueDate: tomorrow.toISOString() });
      },
      addNote: () => {
        const textarea = document.querySelector<HTMLTextAreaElement>('.note-composer textarea');
        textarea?.focus();
      },
      setPriority: (priority: TaskPriority) => void onUpdate({ priority }),
      move: (toScope: Scope) => void onMove(toScope),
      toggleHelp: () => setShowHelp((v) => !v),
    },
    !showHelp
  );

  return (
    <div className="scope-view">
      <ViewHeader
        title="Private"
        searchValue={query}
        onSearchChange={setQuery}
        onNewTask={() => setShowNewTask(true)}
        onShowShortcuts={() => setShowHelp(true)}
      />

      <div className={`pane-wrap ${selected ? 'has-selection' : ''}`}>
        <section className="list-pane" aria-label="Private tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <TaskList
              tasks={sorted}
              selectedTaskId={selected?.taskId ?? null}
              onSelect={select}
              onContextMenu={ctx.openContextMenu}
              onSwipeComplete={async (task) => {
                if (task.status === 'completed') return;
                const updated = await api.updateTask(task.scope, task.taskId, {
                  status: 'completed',
                });
                upsertTask(updated);
              }}
              onSwipeDelete={deleteWithUndo}
              emptyMessage="No private tasks. Right-click a task → Make private to hide it from your team."
            />
          )}
        </section>

        <section className="detail-pane" aria-label="Task detail">
          {selected ? (
            <TaskDetail
              task={selected}
              onUpdate={onUpdate}
              onMove={onMove}
              onDelete={onDelete}
              onAddNote={onAddNote}
              onBack={() => select(null)}
            />
          ) : (
            <div className="muted centered">Select a task (or press j/k).</div>
          )}
        </section>
      </div>

      <CommandBar open={showHelp} onClose={() => setShowHelp(false)} />

      <NewTaskModal
        open={showNewTask}
        defaultScope="personal"
        onClose={() => setShowNewTask(false)}
        onCreate={createTask}
      />

      {ctx.contextMenu && (
        <ContextMenu
          x={ctx.contextMenu.x}
          y={ctx.contextMenu.y}
          items={ctx.buildMenuItems(ctx.contextMenu.task, upsertTask, removeLocalTask, deleteWithUndo)}
          onClose={ctx.closeContextMenu}
        />
      )}

      <MoveScopeDialog
        open={!!ctx.moveDialogFor}
        currentScope={ctx.moveDialogFor?.scope ?? null}
        onSelect={(toScope) => {
          if (ctx.moveDialogFor) {
            void ctx.onMoveScope(ctx.moveDialogFor, toScope, upsertTask, removeLocalTask);
          }
        }}
        onClose={ctx.closeMoveDialog}
      />
    </div>
  );
}
